import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Loader from '@lwt-helix/loader';
import TitleRow from './common/TitleRow';
import ChartToggle from './common/chartToggle';
import OptionsSelector from './common/OptionsSelector';
import ListingViewOptions from './common/ListingViewOptions';
import KeyInformation from './common/KeyInformation';
import ResultsChart from '../components/chart/ResultsChart';
import { marketDynamicsTerms, buttonTerms } from '../../constants';
import { useSearchStore, useCommonStore } from '../../store/store';
import { useMarketDynamicsStore } from '../../store/marketDynamics/store';
import { setStateData } from '../../common/helpers/state';
import { getConfig } from '../helpers/configGenerator';
import { translateListingViewGroups } from '../helpers/listingViews';
import { setDefaultOptionsToChecked, getStatusOptions } from '../helpers/listingStatuses';
import { terms as t } from '../constants';
import { NoResultsMessage } from '../../common/components/search/NoResultsMessage';
import { hasAllZeroValues, getConfigData, getTransformData, getTransformKeyFields,
    addOverviewConfig } from '../helpers/helpers';
import { getRevisedColumnDefs, getRevisedTooltipSettings } from '../helpers/agGrid';
import { getResolvedTooltips, normalizePeriodType } from '../constants/tooltipDefinitions';
import Table from '../../common/components/table/agGrid/Table';
import { getExportButton } from '../../common/helpers/export';
import { exportTypes } from '../helpers/export';
import { listingStatuses as ls } from '../constants/listingStatuses';
import { marketDynamicsColumns } from '../constants/marketDynamicsColumns';
import { officeBreakdownRoute } from '../constants/routes';
import { cdomBreakout } from '../constants/listingViews/cdomBreakout';
import { useFeatureToggles } from '../../common/hooks/featureToggles';
import { exportButtonTypes } from '../../common/constants/export';
import { OverviewTabs } from './OverviewTabs';
import { DownloadResultsModal } from '../../common/components/DownloadResultsModal';
import { listingOptions, overviewStatusKeys as keys, tabIndex } from '../constants/listingViews/overview';
import { usePDFStore } from '../../store/pdf/store';

const MarketDynamics = ({ module, agentData }) => {
    const {
        isLoading,
        loadedSearchCriteria,
        totalsResult,
        listingViewId,
        setMDListingViewId,
        resetBreakdowns,
        mergeStoredOptionsWithDefaults,
        setShouldShowChart,
        shouldShowChart,
        storedChartFilters,
        setMDChartFilters,
        listingStatusByView,
        setMDListingStatusByView,
        clearMDSortByView,
    } = useSearchStore(state => ({
        isLoading: state.isLoading,
        loadedSearchCriteria: state[module]?.loadedSearchCriteria,
        totalsResult: state[module]?.totals,
        listingViewId: state[module]?.listingViewId,
        setMDListingViewId: state.setMDListingViewId,
        resetBreakdowns: state.resetBreakdowns,
        mergeStoredOptionsWithDefaults: state.mergeStoredOptionsWithDefaults,
        setShouldShowChart: state.setShouldShowChart,
        shouldShowChart: state[module]?.shouldShowChart,
        storedChartFilters: state[module]?.chartFilters,
        setMDChartFilters: state.setMDChartFilters,
        listingStatusByView: state[module]?.listingStatusByView,
        setMDListingStatusByView: state.setMDListingStatusByView,
        clearMDSortByView: state.clearMDSortByView,
    }));

    const {
        checkedStatusOptions,
        setSelectedBreakdownPeriod,
        setTableView,
        setCheckedStatusOptions,
        tableView,
        overviewStatus,
        setOverviewStatusKey,
    } = useMarketDynamicsStore(state => ({
        checkedStatusOptions: state.checkedStatusOptions,
        setSelectedBreakdownPeriod: state.setSelectedBreakdownPeriod,
        setTableView: state.setTableView,
        setCheckedStatusOptions: state.setCheckedStatusOptions,
        tableView: state.tableView,
        overviewStatus: state.overviewStatus,
        setOverviewStatusKey: state.setOverviewStatusKey,
    }));

    const overviewTabIds = [ keys.inventory, keys.prices, keys.activity ];
    const overviewCurrentTabId = overviewTabIds[overviewStatus.currentTab];

    const history = useHistory();
    const featureToggles = useFeatureToggles();

    const {
        setBackNavbarTitle,
    } = useCommonStore((state) => ({
        setBackNavbarTitle: state.setBackNavbarTitle,
    }));

    const {
        isPDFLoading,
    } = usePDFStore((state) => ({
        isPDFLoading: state.downloadModalStatus.isPDFLoading,
    }))

    const [state, setState] = useState({
        config: undefined,
        listingViewOptions: undefined,
        overviewStatusOptions: undefined,
        tooltipsSettings: {}
    });

    const gridRef = useRef();

    const listingConfig = getConfigData(listingViewId, state.config);

    const selectedBeginValue = storedChartFilters?.selectedBeginValue;
    const selectedEndValue = storedChartFilters?.selectedEndValue;
    const searchResults = getTransformData(agentData?.marketDynamicsSearch);
    const keysToDisregard = [marketDynamicsColumns.timePeriodStart.id, 'timePeriodEnd'];
    const hasSearchResults = useMemo(() => !hasAllZeroValues(searchResults, keysToDisregard), [searchResults]);
    const isOverview = listingViewId === listingOptions.overview.value;

    const handleListingViewItemClick = id => {
        resetBreakdowns();
        setMDListingViewId(id);
    };

    const shouldHideCdomOption = (option) => {
        const { keyField } = option;
        return option.id === ls.averageCdom.id &&
            searchResults?.every(result => !result?.[keyField]);
    };

    const statusOptions = useMemo(() => {
        if (!state.config || !listingViewId || isOverview) return [];
        const statusDefaultOptions = setDefaultOptionsToChecked(getStatusOptions(state.config, listingViewId), shouldHideCdomOption);
        const storedOptions = listingStatusByView?.[listingViewId];
        return storedOptions?.length > 0
            ? mergeStoredOptionsWithDefaults(statusDefaultOptions, storedOptions)
            : statusDefaultOptions;
    }, [state.config, listingViewId, listingStatusByView]);

    useEffect(() => {
        if (!isOverview) return;

        const overviewSettings = overviewStatus?.chartSettings?.[overviewCurrentTabId];
        const currentTabOptions = overviewSettings?.options?.[0]?.statusOptions ?? {};
        setState(prevState => ({ 
            ...prevState,
            config: prevState?.config 
              ? [overviewSettings, ...prevState.config.slice(1)]
              : [overviewSettings],
            overviewStatusOptions: currentTabOptions
          }));
    }, [isOverview, overviewCurrentTabId]);

    useEffect(() => {
        if (isOverview) {
            const overviewOptions = state.overviewStatusOptions?.groups?.[0]?.options ?? [];
            const transformedKeyFields = getTransformKeyFields(overviewOptions, listingViewId);
            setCheckedStatusOptions(transformedKeyFields.filter(s => s.isDefault) ?? []);
            return;
        }
        if (statusOptions.length > 0) {
            const transformedKeyFields = getTransformKeyFields(statusOptions, listingViewId);
            setCheckedStatusOptions(transformedKeyFields.filter(s => s.checked) ?? []);
        }
    }, [statusOptions, listingViewId, state.overviewStatusOptions]);

    useEffect(() => {
        const timePeriod = loadedSearchCriteria?.timePeriod.intervalType;
        const listingsConfig = getConfig({ timePeriod });
        const overviewConfig = addOverviewConfig(listingsConfig, loadedSearchCriteria);
        const config = [overviewConfig?.inventory, ...listingsConfig];
        setOverviewStatusKey(keys.currentTab, tabIndex.inventory);
        setOverviewStatusKey(keys.chartSettings, overviewConfig);

        const { listingViewOptions, defaultListingViewId, statusOptions: overviewStatusOptions } = translateListingViewGroups({
            listingViewGroups: config,
            selectedListingViewId: null,
            handleListingViewItemClick
        });

        setState(prevState => ({
            ...prevState,
            config,
            listingViewOptions,
            overviewStatusOptions
        }));
        if (!listingViewId) setMDListingViewId(defaultListingViewId);
    }, [loadedSearchCriteria]);

    useEffect(() => {
        const defaultBeginValue = agentData?.marketDynamicsSearch?.slice(-1)[0]?.timePeriodStart ?? '';
        const defaultEndValue = agentData?.marketDynamicsSearch?.[0]?.timePeriodEnd ?? '';
        if (!storedChartFilters?.selectedBeginValue) {
            setMDChartFilters({ selectedBeginValue: defaultBeginValue });
        }
        if (!storedChartFilters?.selectedEndValue) {
            setMDChartFilters({ selectedEndValue: defaultEndValue });
        }
    }, [agentData]);

    useEffect(() => {
        const periodColumnSettings = listingConfig?.groupConfig?.columnSettings;
        const tableView = listingConfig?.listingConfig?.tableSettings;
        const periodType = normalizePeriodType(periodColumnSettings?.label);
        const tooltipsSettings = getRevisedTooltipSettings({
            tableView,
            periodColumnSettings,
            marketDynamicsTooltips: getResolvedTooltips(periodType, tableView?.id)
        });
        const selectedStatusOptionsIds = statusOptions?.filter(status => status.checked)
            .map(status => status.id);
        const revisedColumnDefs = getRevisedColumnDefs(tableView, periodColumnSettings, selectedStatusOptionsIds);
        setTableView({ tableView: revisedColumnDefs });
        useMarketDynamicsStore.setState({ selectedListingView: listingConfig?.listingConfig?.label });
        setStateData('tooltipsSettings', tooltipsSettings, setState);
    }, [listingViewId, loadedSearchCriteria, listingConfig?.groupConfig, statusOptions, selectedBeginValue, selectedEndValue]);


    const getStatusMessage = ({ messageContent }) => {
        return <>
            <TitleRow module={module} />
            <div className="text-center"> {messageContent} </div>
        </>;
    };

    const isInitialSearchNotRun = loadedSearchCriteria === undefined;

    const loader = <div className='py-3'><Loader /></div>;
    const initialSearchNotRun = getStatusMessage({
        messageContent: <p className="initial-search"> {marketDynamicsTerms.searchFilterAdjust} </p>
    });

    if (isLoading || (!searchResults && !isInitialSearchNotRun)) {
        return loader;
    }

    if (isInitialSearchNotRun) {
        return initialSearchNotRun;
    }
    if (!hasSearchResults) {
        return getStatusMessage({ messageContent: <NoResultsMessage /> });
    }

    let listingViewOptions, statusOptionsSelector, chartToggle, exportButton;

    const setSelectedStatusOptions = options => {
        setMDListingStatusByView(listingViewId, options);
        clearMDSortByView(listingViewId);
        const defaultSort = listingConfig?.listingConfig?.tableSettings?.defaultSort;
        gridRef.current?.api?.applyColumnState({
            state: [{ colId: defaultSort.colId, sort: defaultSort.sortDir }],
            defaultState: { sort: null }
        });
    };

    if (state.listingViewOptions?.length) {
        const isDisabled = isPDFLoading;
        listingViewOptions = <ListingViewOptions options={state.listingViewOptions}
            listingViewId={listingViewId}
            title={t.listingViews}
            isDisabled={isDisabled} />;
    }

    if (!isOverview && statusOptions?.length) {
        chartToggle = <ChartToggle shouldShowChart={shouldShowChart}
                                   setShouldShowChart={setShouldShowChart} />;
        statusOptionsSelector = <OptionsSelector id='statusOptions'
            setOptions={setSelectedStatusOptions}
            options={statusOptions}
            title={buttonTerms.statuses} />;
    }

    if (hasSearchResults) {
        const exportDetails = {
            exportType: exportTypes.mdSearchResults,
            listingView: t[listingViewId]
        };
        exportButton = statusOptions 
            && getExportButton({
                exportDetails,
                gridRef,
                type: exportButtonTypes.secondary,
                isDirectPdf: isOverview,
                isLoading: isPDFLoading && isOverview,
            });
    }

    const titleRowProps = {
        exportButton,
        statusOptionsSelector,
        chartToggle,
        listingViewOptions,
        module,
    };
    const handleBarClick = (index) => {
        const isMDOfficeBreakdownEnabled = featureToggles.mdOfficeBreakdown.isEnabled;
        const isCdomBreakoutView = listingViewId === cdomBreakout.value;
        if (!isMDOfficeBreakdownEnabled || isCdomBreakoutView) return;

        const chartData = searchResults.slice().reverse();
        const period = chartData[index].timePeriodStart;
        const formatter = tableView.tables[0].colDef.valueFormatter;
        const valueFormatted = formatter({ value: period });
        setBackNavbarTitle(valueFormatted);
        setSelectedBreakdownPeriod(chartData[index]);
        history.push(officeBreakdownRoute);
    };

    const updatedStatusOptions = getTransformKeyFields(statusOptions, listingViewId);
    const chartData = (searchResults && [...searchResults])?.slice().reverse();

    const getKeyInformation = (options = {}) => {
        const {
            isPDFTable = false,
        } = options;
        return <KeyInformation
            selectedPeriodComparison={storedChartFilters?.selectedPeriodComparison}
            searchResults={searchResults}
            statusOptions={updatedStatusOptions}
            searchCriteria={loadedSearchCriteria}
            listingConfig={listingConfig}
            beginPeriodDate={selectedBeginValue}
            endPeriodDate={selectedEndValue}
            isPDFTable={isPDFTable}
        />;
    };

    const renderChart = (index) => {
        const selectedBeginIndex = chartData?.findIndex(data => data.timePeriodStart === selectedBeginValue);
        const selectedEndIndex = chartData?.findIndex(data => data.timePeriodEnd === selectedEndValue);
        const getResultsChart = (options = {}) => {
            const {
                isPDFChart = false,
            } = options;
            return <ResultsChart
                isOverview={isOverview}
                overviewStatus={overviewStatus}
                data={chartData}
                checkedStatusOptions={checkedStatusOptions}
                config={listingConfig}
                selectedPeriodComparison={storedChartFilters?.selectedPeriodComparison}
                setSelectedPeriodComparison={(value) => setMDChartFilters({ selectedPeriodComparison: value })}
                onBeginChange={(value) => setMDChartFilters({ selectedBeginValue: value })}
                onEndChange={(value) => setMDChartFilters({ selectedEndValue: value })}
                agentData={agentData?.marketDynamicsSearch}
                selectedBeginIndex={selectedBeginIndex}
                selectedEndIndex={selectedEndIndex}
                handleBarClick={handleBarClick}
                isPDFChart={isPDFChart}
                timePeriod={loadedSearchCriteria?.timePeriod.intervalType}
                index={index}
            />;
        };
        return (
            <>
                {getResultsChart()}
                {isPDFLoading && <div className='pdf-chart'>
                    {getResultsChart({ isPDFChart: true })}
                </div>
                }
            </>
        );
    };

    return <>
        <TitleRow  {...titleRowProps} />
        {!isOverview && <DownloadResultsModal />}
        <>
            {isOverview && <OverviewTabs chartData={chartData} renderChart={renderChart} totalsResult={totalsResult}/>}
            {!isOverview && tableView && shouldShowChart && (
                    <div>
                        {renderChart()}
                        {getKeyInformation()}
                        {isPDFLoading && <div className="key-info-capture-area">
                            {getKeyInformation({isPDFTable: true})}
                        </div>
                        }
                    </div>
                )}
        </>
        {!isOverview && searchResults && tableView &&
            <div className="mt-2">
                <Table
                    gridRef={gridRef}
                    tableView={tableView}
                    rowData={searchResults}
                    tableTitleWithTooltips={state.tooltipsSettings}
                />
            </div>
        }
    </>;
};

MarketDynamics.propTypes = {
    module: PropTypes.string,
    agentData: PropTypes.array
};

export default MarketDynamics;
